import { useCallback, useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/lib/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserPlus, Users, Trash2 } from "lucide-react";
import { toast } from "sonner";

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function UsersDialog() {
  const { user: me } = useAuth();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", role: "advisor" });
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e, "Could not load users"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const createUser = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await apiClient.post("/admin/users", form);
      toast.success(`Created ${form.username}`);
      setForm({ username: "", password: "", role: "advisor" });
      load();
    } catch (e2) {
      setErr(formatApiError(e2, "Could not create user"));
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.username}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Could not delete user"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-white/15 bg-transparent hover:bg-white/5 text-white"
          data-testid="admin-users-open-btn"
        >
          <Users className="w-4 h-4 mr-2" />
          Users
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-[#0A0F1C] border border-white/10 text-white sm:max-w-[620px]"
        data-testid="admin-users-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-semibold">
            Manage advisors
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Add, remove and review team members who can access this dashboard.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={createUser}
          className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end mt-2"
        >
          <div className="sm:col-span-1">
            <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
              Username
            </Label>
            <Input
              required
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="mt-2 h-10 bg-white/5 border-white/10 text-white"
              data-testid="new-user-username"
              placeholder="priya"
            />
          </div>
          <div className="sm:col-span-1">
            <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
              Password
            </Label>
            <Input
              required
              type="text"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-2 h-10 bg-white/5 border-white/10 text-white"
              data-testid="new-user-password"
              placeholder="min 8 chars"
              minLength={8}
            />
          </div>
          <div className="sm:col-span-1">
            <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
              Role
            </Label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="mt-2 h-10 w-full rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
              data-testid="new-user-role"
            >
              <option value="advisor">advisor</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <div className="sm:col-span-1">
            <Button
              type="submit"
              className="w-full h-10 bg-[#F97316] hover:bg-[#EA580C] text-white"
              data-testid="new-user-submit"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add
            </Button>
          </div>
        </form>

        {err && (
          <div className="mt-1 text-xs text-red-400" data-testid="new-user-error">
            {err}
          </div>
        )}

        <div className="mt-5 rounded-md border border-white/10 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  Username
                </TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  Role
                </TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  Last login
                </TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="border-white/5">
                  <TableCell className="font-medium text-white">{u.username}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`${
                        u.role === "admin"
                          ? "border-[#F97316]/40 text-[#FDBA74] bg-[#F97316]/10"
                          : "border-white/10 text-slate-300 bg-white/5"
                      }`}
                    >
                      {u.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-400 num-font">
                    {fmtDateTime(u.last_login_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteUser(u)}
                      disabled={u.id === me?.id}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 disabled:opacity-30"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-sm text-slate-500 py-6">
                    No users yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
